import { Link, router } from '@inertiajs/react'
import { useState, useMemo } from 'react'
import { Layout } from '@/components/Layout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ArrowLeft, Pencil, Plus, Server, Activity, CheckCircle, XCircle, Clock, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { DateRangePicker } from '@/components/DateRangePicker'
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'

interface N8nInstance {
  id: number
  name: string
  base_url: string
  last_workflows_synced_at: string | null
  last_executions_synced_at: string | null
}

interface Metrics {
  total_executions: number
  failures: number
  success_rate: number
  time_saved_minutes: number
  time_saved_hours: number
}

interface Client {
  id: number
  name: string
  code: string
  notes: string | null
  instances_count: number
  n8n_instances: N8nInstance[]
  metrics?: Metrics
  created_at: string
  updated_at: string
}

interface ExecutionChartData {
  date: string
  total: number
  successful: number
  failed: number
}

interface TimeSavedChartData {
  date: string
  minutes: number
  hours: number
}

interface DateRange {
  start_date: string
  end_date: string
}

interface Props {
  client: Client
  executions_chart: ExecutionChartData[]
  time_saved_chart: TimeSavedChartData[]
  date_range: DateRange
}

type InstanceSortKey = 'name' | 'base_url' | 'last_workflows_synced_at' | 'last_executions_synced_at'
type SortDirection = 'asc' | 'desc'

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Never'
  return new Date(dateString).toLocaleString()
}

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toString()
}

export default function ClientShow({ client, executions_chart, time_saved_chart, date_range }: Props) {
  const metrics = client.metrics

  // Instance sorting
  const [sortKey, setSortKey] = useState<InstanceSortKey | null>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  const sortedInstances = useMemo(() => {
    if (!sortKey) return client.n8n_instances
    return [...client.n8n_instances].sort((a, b) => {
      let aVal: string | number | null
      let bVal: string | number | null

      if (sortKey === 'last_workflows_synced_at' || sortKey === 'last_executions_synced_at') {
        const aDate = a[sortKey]
        const bDate = b[sortKey]
        aVal = aDate ? new Date(aDate).getTime() : null
        bVal = bDate ? new Date(bDate).getTime() : null
      } else {
        aVal = a[sortKey]
        bVal = b[sortKey]
      }

      if (aVal == null && bVal == null) return 0
      if (aVal == null) return sortDirection === 'asc' ? 1 : -1
      if (bVal == null) return sortDirection === 'asc' ? -1 : 1
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
      }
      return sortDirection === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal))
    })
  }, [client.n8n_instances, sortKey, sortDirection])

  const handleSort = (key: InstanceSortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }

  const SortIcon = ({ column }: { column: InstanceSortKey }) => {
    if (sortKey !== column) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
    return sortDirection === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
  }

  const handleDateRangeChange = (range: { from: Date; to: Date }) => {
    router.get(`/clients/${client.id}`, {
      start_date: range.from.toISOString().split('T')[0],
      end_date: range.to.toISOString().split('T')[0],
    }, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const dateValue = {
    from: new Date(date_range.start_date),
    to: new Date(date_range.end_date),
  }

  const hasExecutions = executions_chart.some(d => d.total > 0)
  const hasTimeSaved = time_saved_chart.some(d => d.minutes > 0)

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link href="/clients">
                <ArrowLeft className="h-4 w-4" />
                <span className="sr-only">Back</span>
              </Link>
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{client.name}</h1>
              <p className="text-muted-foreground">
                <code className="rounded bg-muted px-2 py-0.5 text-sm">{client.code}</code>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <DateRangePicker value={dateValue} onChange={handleDateRangeChange} />
            <Button asChild>
              <Link href={`/clients/${client.id}/edit`}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          </div>
        </div>

        {/* Metrics Cards */}
        {metrics && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Executions</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(metrics.total_executions)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${metrics.success_rate < 80 ? 'text-destructive' : ''}`}>
                  {metrics.total_executions > 0 ? `${metrics.success_rate}%` : '-'}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Failures</CardTitle>
                <XCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-destructive">{formatNumber(metrics.failures)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Time Saved</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{metrics.time_saved_hours}h</div>
                {metrics.time_saved_minutes > 0 && (
                  <p className="text-xs text-muted-foreground">{metrics.time_saved_minutes} minutes</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Charts Row */}
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Executions Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Executions Over Time</CardTitle>
              <CardDescription>Daily execution counts for the selected period</CardDescription>
            </CardHeader>
            <CardContent>
              {hasExecutions ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={executions_chart}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="date" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                      <YAxis className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--background))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '6px',
                        }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="successful"
                        name="Successful"
                        stroke="hsl(142, 76%, 36%)"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="failed"
                        name="Failed"
                        stroke="hsl(var(--destructive))"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex h-[300px] flex-col items-center justify-center text-muted-foreground">
                  <Activity className="h-8 w-8 mb-2" />
                  <p>No execution data for this period</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Time Saved Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Time Saved Over Time</CardTitle>
              <CardDescription>Daily estimated time saved for the selected period</CardDescription>
            </CardHeader>
            <CardContent>
              {hasTimeSaved ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={time_saved_chart}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="date" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                      <YAxis className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} unit="m" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--background))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '6px',
                        }}
                        formatter={(value: number) => [`${value} minutes`, 'Time Saved']}
                      />
                      <Area
                        type="monotone"
                        dataKey="minutes"
                        name="Time Saved"
                        stroke="hsl(217, 91%, 60%)"
                        fill="hsl(217, 91%, 60%)"
                        fillOpacity={0.3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex h-[300px] flex-col items-center justify-center text-muted-foreground">
                  <Clock className="h-8 w-8 mb-2" />
                  <p>No time saved data for this period</p>
                  <p className="text-xs mt-1">Configure time saved values on workflows</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {client.notes && (
          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground whitespace-pre-wrap">{client.notes}</p>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>n8n Instances</CardTitle>
                <CardDescription>
                  Manage n8n instances for this client.
                </CardDescription>
              </div>
              <Button asChild>
                <Link href={`/n8n_instances/new?client_id=${client.id}`}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Instance
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {client.n8n_instances.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8">
                <Server className="h-10 w-10 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-semibold">No instances yet</h3>
                <p className="mt-2 text-center text-muted-foreground">
                  Add an n8n instance to start tracking workflows.
                </p>
                <Button asChild className="mt-4">
                  <Link href={`/n8n_instances/new?client_id=${client.id}`}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Instance
                  </Link>
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <button onClick={() => handleSort('name')} className="flex items-center font-medium hover:text-foreground">
                        Name <SortIcon column="name" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('base_url')} className="flex items-center font-medium hover:text-foreground">
                        URL <SortIcon column="base_url" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('last_workflows_synced_at')} className="flex items-center font-medium hover:text-foreground">
                        Last Workflow Sync <SortIcon column="last_workflows_synced_at" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('last_executions_synced_at')} className="flex items-center font-medium hover:text-foreground">
                        Last Execution Sync <SortIcon column="last_executions_synced_at" />
                      </button>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedInstances.map((instance) => (
                    <TableRow key={instance.id}>
                      <TableCell>
                        <Link
                          href={`/n8n_instances/${instance.id}`}
                          className="font-medium hover:underline"
                        >
                          {instance.name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {instance.base_url}
                      </TableCell>
                      <TableCell>
                        {instance.last_workflows_synced_at ? (
                          <Badge variant="outline">
                            {formatDate(instance.last_workflows_synced_at)}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Never</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {instance.last_executions_synced_at ? (
                          <Badge variant="outline">
                            {formatDate(instance.last_executions_synced_at)}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Never</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}
